import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { PATH } from "../../redux/utils";
import ScrollDateInput from "../../components/calendar/ScrollDateInput";
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  Tooltip, Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { printEmpresaSeccionesPdf, SECTION_COLUMNS } from "../../components/utils/printEmpresaInformePdf";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// ── Paleta ────────────────────────────────────────────────────────────────────
const C = {
  primary:    "#0A539A",
  primarySoft:"rgba(10,83,154,0.08)",
  secondary:  "#0A539A",
  secondarySoft:"rgba(10,83,154,0.08)",
  surface:    "#ffffff",
  bg:         "#F4F6F8",
  border:     "#dde1e7",
  text:       "#1b1b1e",
  muted:      "#5a6072",
  outline:    "#8a909e",
  amber:  "#ffddb5", amberText:"#5c3600",
  teal:   "#93f2f2", tealText:"#003030",
  blue:   "#d8e2ff", blueText:"#071b3b",
  red:    "#ffdad6", redText:"#93000a",
  gray:   "#e8eaed", grayText:"#44474e",
  green:  "#c8f5c8", greenText:"#004d00",
};

// ── Tipos de consulta (tabs) ──────────────────────────────────────────────────
const TIPOS = [
  { key:"empresas",              label:"Empresas" },
  { key:"audiencias",            label:"Audiencias" },
  { key:"expedientes-activos",   label:"Expedientes Activos" },
  { key:"expedientes-resueltos", label:"Expedientes Resueltos" },
  { key:"abogados",              label:"Abogados" },
  { key:"usuarios",              label:"Usuarios" },
];

const MODULOS_OPTIONS = [
  
  { val:"",                       label:"Todos" },
  { val:"Centro de Conciliación", label:"Centro de Conciliación" },
  { val:"Tribunal Laboral",       label:"Tribunal Laboral" },
  { val:"JCA",                    label:"Junta de Conciliación" },
  { val:"Desvinculaciones",       label:"Desvinculaciones" },
];

// Módulos sin Desvinculaciones (para tabs que no lo soportan)
const MODULOS_OPTIONS_BASE = MODULOS_OPTIONS.filter((o) => o.val !== "Desvinculaciones");

const _isTipoDesvinc = (tipo, modulo) =>
  tipo === "expedientes-activos" && modulo === "Desvinculaciones";

// ── Clasificación de conciliaciones (idéntica a useExpedientesProcedimiento) ──
const _norm = (s) =>
  String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();

// IGUAL que getRawStatus: is_constancia_documento=1 → override a "cumplimiento_convenio"
const _getRawStatus = (exp) => {
  if (Number(exp?.is_constancia_documento) === 1) return _norm("cumplimiento_convenio");
  return _norm(exp?.estado || exp?.status || exp?.estatus || "");
};

const _isActivoOrDiferimiento = (exp) => {
  const raw = _getRawStatus(exp);
  return raw.includes("activo") || raw.includes("difer");
};

// IGUAL que isCumplimientoConvenio
const _isCumplimientoConvenio = (exp) => {
  const raw = _getRawStatus(exp);
  const idst = Number(exp?.id_conciliacion_status);
  if (idst === 7) return true;
  if (raw.includes("cumplimiento")) return true;
  if (raw === "cumplimiento_convenio") return true;
  return false;
};

// IGUAL que isConvenioStatus
const _isConvenioStatus = (exp) => {
  if (Number(exp?.is_constancia_documento) === 1) return false;
  const raw = _getRawStatus(exp);
  const idst = Number(exp?.id_conciliacion_status);
  if (raw === "cumplimiento_convenio" || raw.includes("cumplimiento")) return false;
  if (idst === 7) return false;
  return raw === "convenio" || raw.startsWith("convenio") || idst === 2;
};

// IGUAL que isConcluido
const _isConcluido = (exp) => {
  if (_isCumplimientoConvenio(exp)) return false;
  const raw = _getRawStatus(exp);
  return (
    (raw.includes("archivo") && raw.includes("patron")) ||
    (raw.includes("archivo") && raw.includes("solicitante")) ||
    raw.includes("no concili") ||
    raw.includes("incompar") ||
    raw.includes("archivo")
  );
};

// El texto de estado guardado en BD (r.estado) puede no decir literalmente
// "cumplimiento_convenio" aunque is_constancia_documento/id_conciliacion_status=7
// SÍ lo clasifiquen como tal (ver _getRawStatus). Se normaliza aquí para que el
// badge de la tabla, el dropdown ESTADO y el filtro por estado sean consistentes
// con la clasificación real (igual que en Centro de Conciliación).
const _effectiveEstado = (r) => {
  const raw = (r.modulo === "Centro de Conciliación" && _isCumplimientoConvenio(r))
    ? "cumplimiento_convenio"
    : r.estado;
  return toTitleCase(raw);
};

// Solo aplica a registros de Centro de Conciliación; los demás módulos pasan sin filtro
const _clasificarConciliacion = (rows, tipoConsulta) => {
  if (tipoConsulta === "expedientes-activos") {
    return rows.filter((r) =>
      r.modulo !== "Centro de Conciliación" ||
      _isActivoOrDiferimiento(r) ||
      _isConvenioStatus(r)
    );
  }
  if (tipoConsulta === "expedientes-resueltos") {
    return rows.filter((r) =>
      r.modulo !== "Centro de Conciliación" ||
      _isConcluido(r) ||
      _isCumplimientoConvenio(r)
    );
  }
  return rows;
};

// Estados permitidos en el dropdown ESTADO
const _estadoAllow = {
  "expedientes-activos": (e) => {
    const v = _norm(e);
    return v.includes("activo") || v.includes("difer") || v === "convenio" || v.startsWith("convenio");
  },
  "expedientes-resueltos": (e) => {
    const v = _norm(e);
    return v.includes("no concili") || v.includes("incompar") || v.includes("cumpl") || v.includes("archivo");
  },
};

const COL_KEY_MAP = {
  "expedientes-activos":   "expedientes-general",
  "expedientes-resueltos": "expedientes-resueltos",
  audiencias:              "audiencias",
  desvinculaciones:        "desvinculaciones",
  empresas:                "empresas",
  abogados:                "abogados",
  usuarios:                "usuarios",
};

// ── Columnas por tipo ─────────────────────────────────────────────────────────
const COL_DEFS = {
  "expedientes-general": [
    { key:"_num",             label:"#",                req:true,  align:"center", w:40 },
    { key:"ne",               label:"Nº EXPEDIENTE",   req:true,  align:"left",   sort:true },
    { key:"modulo",           label:"MÓDULO",          req:false, align:"left",   sort:true },
    { key:"empresa",          label:"RAZÓN SOCIAL",    req:false, align:"left",   sort:true },
    { key:"trabajador",       label:"TRABAJADOR",      req:false, align:"left",   sort:true },
    { key:"abogado",          label:"RESPONSABLE",     req:false, align:"left",   sort:true },
    { key:"usuario_creador",  label:"USUARIO",         req:false, align:"left",   sort:true },
    { key:"ciudad",           label:"CIUDAD",          req:false, align:"left",   sort:true },
    { key:"autoridad",        label:"AUTORIDAD",       req:false, align:"left",   sort:true },
    { key:"proxima_audiencia",label:"PRÓX. AUDIENCIA", req:false, align:"left",   sort:true },
    { key:"estado",           label:"ESTADO",          req:false, align:"center" },
  ],
  "expedientes-resueltos": [
    { key:"_num",            label:"#",               req:true,  align:"center", w:40 },
    { key:"ne",              label:"Nº EXPEDIENTE",  req:true,  align:"left",   sort:true },
    { key:"modulo",          label:"MÓDULO",         req:false, align:"left",   sort:true },
    { key:"empresa",         label:"RAZÓN SOCIAL",   req:false, align:"left",   sort:true },
    { key:"trabajador",      label:"TRABAJADOR",     req:false, align:"left",   sort:true },
    { key:"abogado",         label:"RESPONSABLE",    req:false, align:"left",   sort:true },
    { key:"usuario_creador", label:"USUARIO",        req:false, align:"left",   sort:true },
    { key:"ciudad",          label:"CIUDAD",         req:false, align:"left",   sort:true },
    { key:"autoridad",       label:"AUTORIDAD",      req:false, align:"left",   sort:true },
    { key:"fecha_resolucion",label:"F. RESOLUCIÓN",  req:false, align:"left",   sort:true },
    { key:"estado",          label:"ESTADO",         req:false, align:"center" },
  ],
  audiencias: [
    { key:"_num",           label:"#",               req:true,  align:"center", w:40 },
    { key:"ne",             label:"Nº EXPEDIENTE",  req:true,  align:"left",   sort:true },
    { key:"empresa",        label:"RAZÓN SOCIAL",   req:false, align:"left",   sort:true },
    { key:"trabajador",     label:"TRABAJADOR",     req:false, align:"left",   sort:true },
    { key:"abogado",        label:"RESPONSABLE",    req:false, align:"left",   sort:true },
    { key:"usuario_creador",label:"USUARIO",        req:false, align:"left",   sort:true },
    { key:"ciudad",         label:"CIUDAD",         req:false, align:"left",   sort:true },
    { key:"autoridad",      label:"AUTORIDAD",      req:false, align:"left",   sort:true },
    { key:"fecha_audiencia",label:"FECHA AUDIENCIA",req:false, align:"left",   sort:true },
    { key:"tipo_audiencia", label:"TIPO",           req:false, align:"left" },
    { key:"resultado",      label:"RESULTADO",      req:false, align:"left" },
    { key:"estado",         label:"ESTADO",         req:false, align:"center" },
  ],
  empresas: [
    { key:"_num",             label:"#",             req:true,  align:"center", w:40 },
    { key:"empresa",          label:"RAZÓN SOCIAL",  req:true,  align:"left",   sort:true },
    { key:"activos",          label:"ACTIVOS",       req:false, align:"right",  sort:true },
    { key:"resueltos",        label:"RESUELTOS",     req:false, align:"right",  sort:true },
    { key:"total",            label:"TOTAL",         req:false, align:"right",  sort:true },
    { key:"abogado_principal",label:"RESPONSABLE",   req:false, align:"left",   sort:true },
    { key:"ciudades_list",    label:"CIUDADES",      req:false, align:"left" },
  ],
  abogados: [
    { key:"_num",                             label:"#",                                  req:true,  align:"center", w:40 },
    { key:"abogado",                          label:"RESPONSABLE",                        req:true,  align:"left",   sort:true },
    { key:"diferimiento",                     label:"DIFERIMIENTO",                       req:false, align:"right",  sort:true },
    { key:"convenio",                         label:"CONVENIO",                           req:false, align:"right",  sort:true },
    { key:"archivo_incomparecencia_trabajador", label:"ARCHIVO INCOMPARECENCIA TRABAJADOR", req:false, align:"right",  sort:true },
    { key:"no_conciliacion",                  label:"NO CONCILIACIÓN",                    req:false, align:"right",  sort:true },
    { key:"constancia_conciliacion",          label:"CUMPLIMIENTO",                       req:false, align:"right",  sort:true },
    { key:"total",                            label:"TOTAL",                              req:false, align:"right",  sort:true },
    { key:"ciudad",                           label:"CIUDADES",                           req:false, align:"left",   sort:true },
  ],
  usuarios: [
    { key:"_num",               label:"#",                  req:true,  align:"center", w:40 },
    { key:"usuario",            label:"USUARIO",            req:true,  align:"left",   sort:true },
    { key:"activos",            label:"ACTIVOS",            req:false, align:"right",  sort:true },
    { key:"resueltos",          label:"RESUELTOS",          req:false, align:"right",  sort:true },
    { key:"total",              label:"TOTAL",              req:false, align:"right",  sort:true },
    { key:"audiencias_creadas", label:"AUDIENCIAS",         req:false, align:"right",  sort:true },
    { key:"expedientes_creados",label:"EXPEDIENTES CREADOS",req:false, align:"right",  sort:true },
    { key:"ciudad",             label:"CIUDADES",           req:false, align:"left",   sort:true },
  ],
  desvinculaciones: [
    { key:"_num",       label:"#",              req:true,  align:"center", w:40 },
    { key:"ne",         label:"Nº FOLIO",       req:true,  align:"left",   sort:true },
    { key:"empresa",    label:"RAZÓN SOCIAL",   req:false, align:"left",   sort:true },
    { key:"trabajador", label:"TRABAJADOR",     req:false, align:"left",   sort:true },
    { key:"abogado",    label:"RESPONSABLE",    req:false, align:"left",   sort:true },
    { key:"ciudad",     label:"CIUDAD",         req:false, align:"left",   sort:true },
    { key:"fecha_baja", label:"FECHA ENCARGO",  req:false, align:"left",   sort:true },
    { key:"estado",     label:"ESTADO",         req:false, align:"center" },
  ],
};

const MOD_COLOR = {
  "Centro de Conciliación": { bg:"#e0f2f1", tc:"#00695c" },
  "Tribunal Laboral":       { bg:"#fce4ec", tc:"#c62828" },
  "JCA":                    { bg:"#eef0ff", tc:"#3949ab" },
};

const getStatusStyle = (estado) => {
  const e = (estado || "").toLowerCase();
  if (e === "activo")
    return { bg: C.blue, tc: C.blueText };
  // Tribunal activos
  if (e.includes("próxima audiencia") || e.includes("proxima audiencia"))
    return { bg: C.amber, tc: C.amberText };
  if (e.includes("pendiente de información") || e.includes("pendiente de informacion") || e.includes("pendiente de notif") || e === "en proceso")
    return { bg: C.blue, tc: C.blueText };
  // Tribunal resueltos
  if (e.includes("desfavorable"))
    return { bg: C.red, tc: C.redText };
  if (e.includes("caducidad") || e.includes("sobreseimiento") || e.includes("incompetencia") || e.includes("acumulaci"))
    return { bg: C.gray, tc: C.grayText };
  if (e.includes("desistimiento"))
    return { bg: C.teal, tc: C.tealText };
  if (e.includes("cumplimiento pendiente"))
    return { bg: C.amber, tc: C.amberText };
  // Comunes
  if (e.includes("convenio") || e.includes("conciliado") || e.includes("resuelto") || e.includes("favorable"))
    return { bg: C.green, tc: C.greenText };
  if (e.includes("diferimiento") || e.includes("apelacion") || e.includes("programada") || e.includes("celebrada"))
    return { bg: C.amber, tc: C.amberText };
  if (e.includes("critico") || e.includes("término cr") || e.includes("termino cr") || e.includes("impugnado") || e.includes("no concil") || e.includes("incompar"))
    return { bg: C.red, tc: C.redText };
  if (e.includes("cumpl"))
    return { bg: C.teal, tc: C.tealText };
  if (e.includes("archivado") || e.includes("resultado"))
    return { bg: C.gray, tc: C.grayText };
  return { bg: C.gray, tc: C.grayText };
};

const authHeader = () => {
  const token = localStorage.getItem("tokenadmin") || localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const MESES_ES = [
  "enero","febrero","marzo","abril","mayo","junio",
  "julio","agosto","septiembre","octubre","noviembre","diciembre",
];

const fmtDate = (v) => {
  if (!v) return null;
  const str = String(v);
  let datePart = str;
  let timePart = null;

  if (str.includes("T")) {
    [datePart, timePart] = str.split("T");
  } else if (/\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(str)) {
    [datePart, timePart] = str.split(" ");
  }

  if (!datePart.includes("-")) return str;
  const [y, m, d] = datePart.split("-");
  const mes = MESES_ES[parseInt(m, 10) - 1];
  if (!mes) return str;

  const dia = String(parseInt(d, 10)).padStart(2, "0");
  const base = `${dia} de ${mes} de ${y}`;

  if (timePart) {
    const hhmm = timePart.slice(0, 5);
    if (hhmm && hhmm !== "00:00") return `${base} a las ${hhmm} horas`;
  }
  return base;
};

const fmtDateISO = (s) => {
  if (!s) return "—";
  const [y, m, d] = s.split("-");
  return `${d}/${m}/${y}`;
};

// Agrupa filas por mes (más reciente primero) e intercala un encabezado { __mes }
// antes de las filas de cada mes. Las filas sin fecha van al final.
const conEncabezadosMes = (rows, dateKey) => {
  const grupos = new Map();
  rows.forEach((r) => {
    const f = String(r[dateKey] ?? "");
    const key = /^\d{4}-\d{2}/.test(f) ? f.slice(0, 7) : "sin-fecha";
    if (!grupos.has(key)) grupos.set(key, []);
    grupos.get(key).push(r);
  });
  const keys = [...grupos.keys()].sort((a, b) => {
    if (a === "sin-fecha") return 1;
    if (b === "sin-fecha") return -1;
    return b.localeCompare(a);
  });
  return keys.flatMap((key) => {
    let label = "Sin fecha";
    if (key !== "sin-fecha") {
      const [y, m] = key.split("-");
      const mes = MESES_ES[parseInt(m, 10) - 1];
      label = `${mes.charAt(0).toUpperCase()}${mes.slice(1)} ${y}`;
    }
    const items = grupos.get(key);
    return [{ __mes: true, label, count: items.length }, ...items];
  });
};

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("es-MX", { style:"currency", currency:"MXN" });
};

// Primera letra de cada palabra en mayúscula (para nombres, razones sociales, estados, etc.)
const toTitleCase = (s) => {
  if (!s) return s;
  return String(s)
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/(^|[\s.,/-])([a-záéíóúñü0-9])/g, (m, sep, c) => sep + c.toUpperCase());
};

// Texto largo cortado con "…" a los max px, con el texto completo en tooltip
function Trunc({ text, max = 220, style }) {
  if (!text) return <span style={{ color:C.outline }}>—</span>;
  return (
    <span title={text} style={{ display:"block", maxWidth:max, overflow:"hidden",
      textOverflow:"ellipsis", whiteSpace:"nowrap", ...style }}>
      {text}
    </span>
  );
}

// ── DatePickerES — input de fecha con display en español ──────────────────────
function DatePickerES({ value, onChange, label }) {
  const [open, setOpen] = useState(false);
  const formatted = value ? fmtDate(value) : null;
  return (
    <div>
      <label style={{ display:"block", fontSize:10, fontWeight:700, letterSpacing:"0.06em",
        color:"#5a6072", marginBottom:4, textTransform:"uppercase" }}>{label}</label>
      <div style={{ position:"relative" }}>
        <div
          onClick={() => setOpen(true)}
          style={{ width:"100%", height:34, padding:"0 10px", border:"1px solid #dde1e7",
            borderRadius:5, fontSize:12, boxSizing:"border-box",
            display:"flex", alignItems:"center",
            color: formatted ? "#1b1b1e" : "#8a909e",
            background:"#ffffff", fontFamily:"inherit", cursor:"pointer",
            whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}
        >
          {formatted || "dd/mm/aaaa"}
        </div>
        <ScrollDateInput
          open={open}
          onOpenChange={setOpen}
          value={value}
          onChange={(v) => {
            onChange?.({ target: { value: v } });
            setOpen(false);
          }}
          style={{ position:"absolute", inset:0, opacity:0, pointerEvents:"none", width:0, height:0 }}
        />
      </div>
    </div>
  );
}

// ── Multiselect ───────────────────────────────────────────────────────────────
function Multiselect({ options, selected, onChange, label, placeholder, withSearch }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState("");
  const ref               = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const filtered = options.filter((o) => o.toLowerCase().includes(query.toLowerCase()));
  const toggle = (val) =>
    onChange(selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val]);
  const allFilteredSelected = filtered.length > 0 && filtered.every((o) => selected.includes(o));
  const toggleAll = () => {
    if (allFilteredSelected) {
      onChange(selected.filter((v) => !filtered.includes(v)));
    } else {
      onChange([...new Set([...selected, ...filtered])]);
    }
  };

  const hasActive = selected.length > 0;
  const btnLabel = !hasActive
    ? placeholder
    : selected.length === 1
      ? selected[0]
      : `${selected.length} seleccionados`;

  return (
    <div ref={ref} style={{ position:"relative", flex:1 }}>
      <label style={S.label}>{label}</label>
      <button type="button" onClick={() => setOpen((v) => !v)}
        style={{ ...S.input, display:"flex", justifyContent:"space-between", alignItems:"center",
          cursor:"pointer", padding:"0 10px",
          borderColor: hasActive ? C.secondary : C.border,
          background:  hasActive ? C.secondarySoft : C.surface }}>
        <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
          fontSize:13, fontWeight: hasActive ? 600 : 400,
          color: hasActive ? C.secondary : C.outline }}>
          {btnLabel}
        </span>
        <span style={{ fontSize:13, marginLeft:4, flexShrink:0, color: hasActive ? C.secondary : C.outline,
          transform: open ? "rotate(180deg)" : "none", transition:"transform 0.15s" }}>▾</span>
      </button>
      {open && (
        <div style={S.dropdown}>
          {withSearch && (
            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar..."
              style={{ padding:"7px 10px", border:"none", borderBottom:`1px solid ${C.border}`,
                fontSize:12, outline:"none", width:"100%", boxSizing:"border-box", fontFamily:"inherit" }} />
          )}
          <div onClick={toggleAll}
            style={{ padding:"6px 12px", fontSize:11, fontWeight:700, letterSpacing:"0.05em",
              color:C.secondary, cursor:"pointer", borderBottom:`1px solid ${C.border}`, userSelect:"none" }}>
            {allFilteredSelected ? "Limpiar selección" : "Seleccionar todo"}
          </div>
          <div style={{ maxHeight:200, overflowY:"auto" }}>
            {filtered.map((opt) => (
              <label key={opt} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 12px",
                cursor:"pointer", fontSize:13, color:C.text,
                background: selected.includes(opt) ? C.secondarySoft : "transparent" }}>
                <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)}
                  style={{ accentColor:C.secondary, cursor:"pointer" }} />
                {opt}
              </label>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding:"10px 12px", color:C.outline, fontSize:12, textAlign:"center" }}>
                Sin resultados
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
const THIS_YEAR = new Date().getFullYear();
const DEFAULT_FI = `${THIS_YEAR}-01-01`;
const DEFAULT_FF = `${THIS_YEAR}-12-31`;

export default function InformesPage() {

  const [tipoConsulta, setTipoConsulta] = useState("empresas");

  // Filtros pending (UI)
  const [fechaInicio,  setFechaInicio]  = useState(DEFAULT_FI);
  const [fechaFin,     setFechaFin]     = useState(DEFAULT_FF);
  const [selModulo,    setSelModulo]    = useState("");
  const [selEmpresa,   setSelEmpresa]   = useState([]);
  const [selAbogado,   setSelAbogado]   = useState([]);
  const [selUsuario,   setSelUsuario]   = useState([]);
  const [selCiudad,    setSelCiudad]    = useState([]);
  const [selAutoridad, setSelAutoridad] = useState([]);
  const [selEstado,    setSelEstado]    = useState([]);

  // Catálogos reales
  const [catEmpresas,   setCatEmpresas]   = useState([]);
  const [catAbogados,   setCatAbogados]   = useState([]);
  const [catUsuarios,   setCatUsuarios]   = useState([]);
  const [catCiudades,   setCatCiudades]   = useState([]);
  const [autsPorTipo,   setAutsPorTipo]   = useState({});
  const [autsPorCiudad, setAutsPorCiudad] = useState({});
  const [catLoading,    setCatLoading]    = useState(true);

  // Datos tabla
  const [tableData,    setTableData]    = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableError,   setTableError]   = useState(null);

  // Vista: tabla o gráficas
  const [viewMode, setViewMode] = useState("tabla");

  // Datos para la vista de gráficas
  const [graficasData,    setGraficasData]    = useState(null);
  const [graficasLoading, setGraficasLoading] = useState(false);

  // Meta de la última consulta aplicada
  const [queryMeta, setQueryMeta] = useState({ fechaInicio: DEFAULT_FI, fechaFin: DEFAULT_FF });

  // Tabla: orden y paginación
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [page,    setPage]    = useState(1);
  const [rowsPP,  setRowsPP]  = useState(25);

  const debounceRef  = useRef(null);
  const mountedRef   = useRef(false);
  const colPanelRef  = useRef(null);
  const dragSrcRef   = useRef(null);
  const [colPanelOpen, setColPanelOpen] = useState(false);
  const [visibleCols,  setVisibleCols]  = useState(null);
  const [colOrder,     setColOrder]     = useState(null);
  const [dragOverKey,  setDragOverKey]  = useState(null);

  // Detalle de empresa (drill-down desde la tabla Empresas)
  const [empDetalle,        setEmpDetalle]        = useState(null);
  const [empDetalleData,    setEmpDetalleData]    = useState(null);
  const [empDetalleLoading, setEmpDetalleLoading] = useState(false);

  // Cargar catálogos al montar
  useEffect(() => {
    setCatLoading(true);
    fetch(`${PATH}/informes/catalogos`, { headers: authHeader() })
      .then((r) => r.json())
      .then((d) => {
        setCatEmpresas(d.empresas || []);
        setCatAbogados(d.abogados || []);
        setCatUsuarios(d.usuarios || []);
        setCatCiudades(d.ciudades || []);
        setAutsPorTipo(d.autoridades_por_tipo || {});
        setAutsPorCiudad(d.autoridades_por_ciudad || {});
      })
      .catch(console.error)
      .finally(() => setCatLoading(false));
  }, []);

  const fetchTable = useCallback(async (payload) => {
    setTableLoading(true);
    setTableError(null);
    try {
      const resp = await fetch(`${PATH}/informes/tabla`, {
        method: "POST",
        headers: { "Content-Type":"application/json", ...authHeader() },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setTableData(data.items || []);
    } catch (err) {
      setTableError(err.message);
      setTableData([]);
    } finally {
      setTableLoading(false);
    }
  }, []);

  const fetchEmpresaDetalle = useCallback(async (empresa, fi, ff, filtros) => {
    setEmpDetalleLoading(true);
    try {
      const resp = await fetch(`${PATH}/informes/empresa-detalle`, {
        method: "POST",
        headers: { "Content-Type":"application/json", ...authHeader() },
        body: JSON.stringify({
          empresa, fecha_inicio: fi, fecha_fin: ff,
          modulos:     filtros?.modulos     || [],
          abogados:    filtros?.abogados    || [],
          usuarios:    filtros?.usuarios    || [],
          ciudades:    filtros?.ciudades    || [],
          autoridades: filtros?.autoridades || [],
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setEmpDetalleData(await resp.json());
    } catch (err) {
      console.error("empresa-detalle:", err);
      setEmpDetalleData(null);
    } finally {
      setEmpDetalleLoading(false);
    }
  }, []);

  const openEmpresaDetalle = (empresa) => {
    setEmpDetalle(empresa);
    fetchEmpresaDetalle(empresa, queryMeta.fechaInicio, queryMeta.fechaFin, {
      modulos:     selModulo ? [selModulo] : [],
      abogados:    selAbogado,
      usuarios:    selUsuario,
      ciudades:    selCiudad,
      autoridades: selAutoridad,
    });
  };

  const closeEmpresaDetalle = () => {
    setEmpDetalle(null);
    setEmpDetalleData(null);
  };

  const fetchGraficas = useCallback(async (payload) => {
    setGraficasLoading(true);
    try {
      const resp = await fetch(`${PATH}/informes/graficas`, {
        method: "POST",
        headers: { "Content-Type":"application/json", ...authHeader() },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setGraficasData(await resp.json());
    } catch (err) {
      console.error("graficas:", err);
      setGraficasData(null);
    } finally {
      setGraficasLoading(false);
    }
  }, []);

  // Carga inicial al cambiar tab (selModulo siempre "" al cambiar tab)
  useEffect(() => {
    const payload = {
      tipo: tipoConsulta,
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
      modulos: [],
      empresas: selEmpresa,
      abogados: selAbogado,
      usuarios: selUsuario,
      ciudades: selCiudad,
      autoridades: selAutoridad,
    };
    setPage(1); setSortCol(null);
    fetchTable(payload);
    // eslint-disable-next-line
  }, [tipoConsulta]);

  // Auto-aplicar cuando cambia cualquier filtro (sin botón)
  useEffect(() => {
    if (!mountedRef.current) { mountedRef.current = true; return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const dv = _isTipoDesvinc(tipoConsulta, selModulo);
      const payload = {
        tipo: dv ? "desvinculaciones" : tipoConsulta,
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
        modulos: dv ? [] : (selModulo ? [selModulo] : []),
        empresas: selEmpresa,
        abogados: selAbogado,
        usuarios: selUsuario,
        ciudades: selCiudad,
        autoridades: dv ? [] : selAutoridad,
      };
      setSelEstado([]); setColOrder(null);
      setPage(1); setSortCol(null);
      setQueryMeta({ fechaInicio, fechaFin });
      closeEmpresaDetalle();
      fetchTable(payload);
      if (viewMode === "graficas") fetchGraficas(payload);
      else setGraficasData(null);
    }, 300);
    // eslint-disable-next-line
  }, [fechaInicio, fechaFin, selModulo, selEmpresa, selAbogado, selUsuario, selCiudad, selAutoridad]);

  useEffect(() => {
    const h = (e) => { if (colPanelRef.current && !colPanelRef.current.contains(e.target)) setColPanelOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => { setPage(1); }, [selEstado]);

  const buildPayload = () => {
    const dv = _isTipoDesvinc(tipoConsulta, selModulo);
    return {
      tipo: dv ? "desvinculaciones" : tipoConsulta,
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
      modulos: dv ? [] : (selModulo ? [selModulo] : []),
      empresas: selEmpresa,
      abogados: selAbogado,
      usuarios: selUsuario,
      ciudades: selCiudad,
      autoridades: dv ? [] : selAutoridad,
    };
  };

  const applyFilters = () => {
    const payload = buildPayload();
    setQueryMeta({ fechaInicio, fechaFin });
    setPage(1); setSortCol(null);
    closeEmpresaDetalle();
    fetchTable(payload);
    if (viewMode === "graficas") fetchGraficas(payload);
  };

  const clearFilters = () => {
    setFechaInicio(DEFAULT_FI); setFechaFin(DEFAULT_FF);
    setSelModulo(""); setSelEmpresa([]); setSelAbogado([]); setSelUsuario([]);
    setSelCiudad([]); setSelAutoridad([]); setSelEstado([]);
  };

  const removeChip = (type, val) => {
    if (type === "empresa")   setSelEmpresa(prev  => prev.filter(v => v !== val));
    if (type === "abogado")   setSelAbogado(prev  => prev.filter(v => v !== val));
    if (type === "usuario")   setSelUsuario(prev  => prev.filter(v => v !== val));
    if (type === "ciudad")    setSelCiudad(prev   => prev.filter(v => v !== val));
    if (type === "autoridad") setSelAutoridad(prev => prev.filter(v => v !== val));
    if (type === "estado")    setSelEstado(prev   => prev.filter(v => v !== val));
  };

  const handleSort = (key) => {
    if (sortCol === key) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortCol(key); setSortDir("asc"); }
    setPage(1);
  };

  const handleTipoChange = (key) => {
    setTipoConsulta(key);
    setSelModulo(""); setSelEmpresa([]); setSelAbogado([]); setSelUsuario([]);
    setSelCiudad([]); setSelAutoridad([]); setSelEstado([]);
    setGraficasData(null);
    setViewMode("tabla");
    setVisibleCols(null);
    setColOrder(null);
    setColPanelOpen(false);
    closeEmpresaDetalle();
  };

  // Autoridades filtradas según módulo y ciudades seleccionados
  const catAutoridades = useMemo(() => {
    const TIPO_KEY = {
      "Centro de Conciliación": "conciliacion",
      "Tribunal Laboral":       "tribunal",
      "JCA":                    "junta_conciliacion",
    };
    const key = TIPO_KEY[selModulo];
    const byModulo = new Set(
      key ? (autsPorTipo[key] || []) : Object.values(autsPorTipo).flat()
    );
    if (selCiudad.length === 0) return [...byModulo].sort();
    const byCiudad = new Set(selCiudad.flatMap((c) => autsPorCiudad[c] || []));
    return [...byModulo].filter((a) => byCiudad.has(a)).sort();
  }, [autsPorTipo, autsPorCiudad, selModulo, selCiudad]);

  // Limpiar autoridades seleccionadas que ya no pertenezcan al módulo activo
  useEffect(() => {
    if (selAutoridad.length === 0) return;
    setSelAutoridad((prev) => prev.filter((a) => catAutoridades.includes(a)));
  }, [catAutoridades]);

  // Derivados
  const isDesvinc = _isTipoDesvinc(tipoConsulta, selModulo);
  const showRow2 = tipoConsulta !== "empresas" && tipoConsulta !== "usuarios";
  const cols = COL_DEFS[isDesvinc ? "desvinculaciones" : (COL_KEY_MAP[tipoConsulta] || "expedientes-general")] || [];
  const ALWAYS_VIS = ["_num", "ne"];

  const orderedCols = useMemo(() => {
    if (!colOrder) return cols;
    return [...cols].sort((a, b) => {
      const ai = colOrder.indexOf(a.key);
      const bi = colOrder.indexOf(b.key);
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }, [cols, colOrder]);

  const displayCols = visibleCols === null
    ? orderedCols
    : orderedCols.filter((c) => ALWAYS_VIS.includes(c.key) || visibleCols.has(c.key));

  const orderedPanelCols = orderedCols.filter((c) => c.key !== "_num" && c.key !== "modulo");

  const isColVisible = (key) => visibleCols === null || visibleCols.has(key);
  const toggleColVis = (key) => {
    setVisibleCols((prev) => {
      const cur = prev ?? new Set(cols.map((c) => c.key));
      const next = new Set(cur);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const reorderCol = (srcKey, tgtKey) => {
    if (!srcKey || srcKey === tgtKey) return;
    setColOrder((prev) => {
      const base = prev ?? orderedPanelCols.map((c) => c.key);
      const arr = [...base];
      const si = arr.indexOf(srcKey);
      const ti = arr.indexOf(tgtKey);
      if (si === -1 || ti === -1) return prev;
      arr.splice(si, 1);
      arr.splice(ti, 0, srcKey);
      return arr;
    });
  };

  const classifiedData = useMemo(() =>
    _clasificarConciliacion(tableData, tipoConsulta).map((r) => ({ ...r, estado: _effectiveEstado(r) })),
  [tableData, tipoConsulta]);

  const catEstados = useMemo(() => {
    const set = new Set();
    classifiedData.forEach((r) => { if (r.estado) set.add(r.estado); });
    // Siempre disponibles en Audiencias, aunque el rango filtrado no traiga registros
    if (tipoConsulta === "audiencias") {
      ["1era Audiencia", "2da Audiencia", "3ra Audiencia"].forEach((s) => set.add(s));
    }
    return [...set].sort();
  }, [classifiedData, tipoConsulta]);

  // Opciones visibles en el filtro ESTADO, restringidas por tipo de consulta
  // Para Tribunal los estados vienen calculados del backend; para módulo mixto ("") también se muestran todos
  const estadoOptions = useMemo(() => {
    if (isDesvinc || selModulo === "Tribunal Laboral" || !selModulo) return catEstados;
    const fn = _estadoAllow[tipoConsulta];
    return fn ? catEstados.filter(fn) : catEstados;
  }, [catEstados, tipoConsulta, isDesvinc, selModulo]);

  const sortedData = useMemo(() => {
    if (!sortCol) return classifiedData;
    return [...classifiedData].sort((a, b) => {
      let va = a[sortCol] ?? "";
      let vb = b[sortCol] ?? "";
      if (typeof va === "number" && typeof vb === "number")
        return sortDir === "asc" ? va - vb : vb - va;
      va = String(va).toLowerCase(); vb = String(vb).toLowerCase();
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ?  1 : -1;
      return 0;
    });
  }, [classifiedData, sortCol, sortDir]);

  const filteredData = useMemo(() => {
    if (!selEstado.length) return sortedData;
    return sortedData.filter((r) => selEstado.includes(r.estado));
  }, [sortedData, selEstado]);

  const total      = filteredData.length;
  const start      = total === 0 ? 0 : (page - 1) * rowsPP + 1;
  const end        = Math.min(page * rowsPP, total);
  const pageData   = filteredData.slice((page - 1) * rowsPP, page * rowsPP);
  const totalPages = Math.max(1, Math.ceil(total / rowsPP));

  const activeChips = [
    ...selEmpresa.map((v)   => ({ type:"empresa",   val:v })),
    ...selAbogado.map((v)   => ({ type:"abogado",   val:v })),
    ...selUsuario.map((v)   => ({ type:"usuario",   val:v })),
    ...selCiudad.map((v)    => ({ type:"ciudad",    val:v })),
    ...selAutoridad.map((v) => ({ type:"autoridad", val:v })),
    ...selEstado.map((v)    => ({ type:"estado",    val:v })),
  ];

  // Chips agrupados por categoría para no mezclar visualmente los filtros
  const CHIP_TYPE_LABELS = {
    empresa:   "Razón Social",
    abogado:   "Responsable",
    usuario:   "Usuario",
    ciudad:    "Ciudad",
    autoridad: "Autoridad",
    estado:    "Estado",
  };
  const chipGroups = useMemo(() => {
    const order = ["empresa", "ciudad", "autoridad", "abogado", "usuario", "estado"];
    return order
      .map((type) => ({ type, items: activeChips.filter((c) => c.type === type) }))
      .filter((g) => g.items.length > 0);
  }, [selEmpresa, selAbogado, selUsuario, selCiudad, selAutoridad, selEstado]);

  // Estadísticas cliente: sólo para KPIs y gráficas basadas en tableData
  const chartStats = useMemo(() => {
    if (!tableData.length) return null;
    const count = (field) => {
      const m = {};
      tableData.forEach((r) => { if (r[field]) m[r[field]] = (m[r[field]] || 0) + 1; });
      return Object.entries(m).sort((a, b) => b[1] - a[1]);
    };
    return {
      topEmpresas: count("empresa").slice(0, 10),
      porCiudad:   count("ciudad").slice(0, 10),
    };
  }, [tableData]);

  const titleLabel = TIPOS.find((t) => t.key === tipoConsulta)?.label || "";

  // ── Exportaciones ──────────────────────────────────────────────────────────
  const exportCols = cols.filter((c) => c.key !== "_num");

  const exportCSV = useCallback(() => {
    const data = selEstado.length ? classifiedData.filter((r) => selEstado.includes(r.estado)) : classifiedData;
    const headers = exportCols.map((c) => c.label);
    const rows = data.map((row) =>
      exportCols.map((c) => {
        const v = row[c.key] ?? "";
        const s = String(v);
        return s.includes(",") || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
      })
    );
    const BOM = "﻿";
    const csv = BOM + [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${titleLabel}_${queryMeta.fechaInicio}_${queryMeta.fechaFin}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [tableData, selEstado, exportCols, titleLabel, queryMeta]);

  const exportPDF = useCallback(() => {
    const data = selEstado.length ? classifiedData.filter((r) => selEstado.includes(r.estado)) : classifiedData;
    const colList = exportCols;
    const headerRow = colList.map((c) => `<th>${c.label}</th>`).join("");
    const bodyRows = data.map((row) =>
      `<tr>${colList.map((c) => `<td>${row[c.key] ?? "—"}</td>`).join("")}</tr>`
    ).join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>${titleLabel}</title>
      <style>
        *{box-sizing:border-box}
        body{font-family:Arial,sans-serif;font-size:10px;margin:12mm}
        h2{color:#031636;font-size:13px;margin:0 0 2px}
        .sub{color:#5a6072;font-size:9px;margin:0 0 10px}
        table{width:100%;border-collapse:collapse;font-size:9px}
        th{background:#031636;color:#fff;padding:5px 6px;text-align:left;font-size:8px;letter-spacing:.04em}
        td{padding:4px 6px;border-bottom:1px solid #dde1e7}
        tr:nth-child(even) td{background:#f8f9fb}
        @page{margin:12mm}
      </style></head><body>
      <h2>${titleLabel}</h2>
      <p class="sub">${queryMeta.fechaInicio} – ${queryMeta.fechaFin} · ${total} registros</p>
      <table><thead><tr>${headerRow}</tr></thead><tbody>${bodyRows}</tbody></table>
    </body></html>`;
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) { alert("Permite ventanas emergentes para exportar PDF."); return; }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 400);
  }, [tableData, selEstado, exportCols, titleLabel, queryMeta, total]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ background:C.bg, minHeight:"100%", fontFamily:"var(--app-font-sans)" }}>

      {/* Encabezado */}
      <div style={{ background:C.surface, borderBottom:`1px solid ${C.border}`, padding:"16px 24px 0" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
          <div>
            <h2 style={{ fontSize:20, fontWeight:700, color:C.primary, margin:0, letterSpacing:"-0.01em" }}>
              Informes y Estadísticas
            </h2>
            <p style={{ fontSize:12, color:C.muted, margin:"2px 0 0" }}>
              Consulta y exportación de datos operativos
            </p>
          </div>
          {catLoading && (
            <span style={{ fontSize:11, color:C.outline, fontStyle:"italic", alignSelf:"center" }}>
              Cargando catálogos...
            </span>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", borderTop:`1px solid ${C.border}`,
          marginLeft:-24, marginRight:-24, paddingLeft:24, overflowX:"auto" }}>
          {TIPOS.map((t) => {
            const active = tipoConsulta === t.key;
            return (
              <button key={t.key} type="button" onClick={() => handleTipoChange(t.key)}
                style={{ padding:"10px 18px", background:"transparent", border:"none",
                  borderBottom: active ? `2px solid ${C.secondary}` : "2px solid transparent",
                  color: active ? C.secondary : C.muted, fontWeight: active ? 700 : 500,
                  fontSize:13, cursor:"pointer", fontFamily:"inherit", marginBottom:-1,
                  whiteSpace:"nowrap", transition:"color 0.15s, border-color 0.15s" }}>
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ padding:20 }}>

        {/* Filtros */}
        <div style={{ ...S.card, marginBottom:12 }}>
          {/* Fila 1: fechas + razón social + ciudad + autoridad + módulo + limpiar */}
          <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom: showRow2 ? 12 : 0 }}>
            <div style={{ flex:"1 1 130px", maxWidth:190 }}>
              <DatePickerES label="FECHA INICIO" value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)} />
            </div>
            <div style={{ flex:"1 1 130px", maxWidth:190 }}>
              <DatePickerES label="FECHA FIN" value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)} />
            </div>
            <div style={{ flex:"2 1 160px" }}>
              <Multiselect label="RAZÓN SOCIAL" placeholder="Todas"
                options={catEmpresas} selected={selEmpresa} onChange={setSelEmpresa} withSearch />
            </div>
            <div style={{ flex:"1 1 160px" }}>
              <Multiselect label="CIUDAD" placeholder="Todas"
                options={catCiudades} selected={selCiudad} onChange={setSelCiudad} />
            </div>
            {tipoConsulta === "empresas" && (
              <div style={{ flex:"1 1 160px" }}>
                <Multiselect label="RESPONSABLE" placeholder="Todos"
                  options={catAbogados} selected={selAbogado} onChange={setSelAbogado} withSearch />
              </div>
            )}
            <div style={{ flex:"1 1 160px", opacity: isDesvinc ? 0.4 : 1, pointerEvents: isDesvinc ? "none" : "auto",
              position:"relative" }}>
              <Multiselect label={`AUTORIDAD${isDesvinc ? " (N/A)" : ""}`} placeholder="Todas"
                options={catAutoridades} selected={isDesvinc ? [] : selAutoridad}
                onChange={isDesvinc ? () => {} : setSelAutoridad} withSearch />
            </div>
            {(tipoConsulta === "expedientes-activos" || tipoConsulta === "expedientes-resueltos" || tipoConsulta === "audiencias" || tipoConsulta === "usuarios") && (
              <div style={{ flex:"1 1 150px", position:"relative" }}>
                <label style={S.label}>MÓDULO</label>
                <select value={selModulo} onChange={(e) => setSelModulo(e.target.value)} style={S.select}>
                  {(tipoConsulta === "expedientes-activos" ? MODULOS_OPTIONS : MODULOS_OPTIONS_BASE).map((o) => (
                    <option key={o.val} value={o.val}>{o.label}</option>
                  ))}
                </select>
                <span style={S.selArrow}>▾</span>
              </div>
            )}
            <div style={{ display:"flex", gap:8, marginLeft:"auto", alignItems:"flex-end", flexShrink:0 }}>
              <button type="button" onClick={clearFilters} style={{ ...S.btnGhost, padding:"0 24px" }}>Limpiar</button>
            </div>
          </div>
          {showRow2 && (
            <>
              <div style={{ height:1, background:C.border, margin:"0 -16px 12px" }} />
              {/* Fila 2: estado + responsable */}
              <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
                {tipoConsulta !== "empresas" && tipoConsulta !== "abogados" && tipoConsulta !== "usuarios" && (
                  <Multiselect label="ESTADO" placeholder="Todos"
                    options={estadoOptions} selected={selEstado} onChange={setSelEstado} />
                )}
                {tipoConsulta !== "empresas" && tipoConsulta !== "usuarios" && (
                  <Multiselect label="RESPONSABLE" placeholder="Todos"
                    options={catAbogados} selected={selAbogado} onChange={setSelAbogado} withSearch />
                )}
                {(tipoConsulta === "expedientes-activos" || tipoConsulta === "expedientes-resueltos" || tipoConsulta === "audiencias") && (
                  <Multiselect label="USUARIO" placeholder="Todos"
                    options={catUsuarios} selected={selUsuario} onChange={setSelUsuario} withSearch />
                )}
              </div>
            </>
          )}
        </div>

        {/* Chips de filtros activos, agrupados por categoría */}
        {activeChips.length > 0 && (
          <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:12, alignItems:"center" }}>
            <span style={{ fontSize:11, color:C.muted, fontWeight:600, letterSpacing:"0.04em", alignSelf:"center" }}>
              FILTROS ACTIVOS:
            </span>
            {chipGroups.map((group) => (
              <div key={group.type}
                style={{ display:"inline-flex", alignItems:"center", gap:6, background:C.surface,
                  border:`1px solid ${C.border}`, borderRadius:20, padding:"3px 10px 3px 10px" }}>
                <span style={{ fontSize:10, fontWeight:700, letterSpacing:"0.04em", color:C.outline,
                  textTransform:"uppercase", whiteSpace:"nowrap" }}>
                  {CHIP_TYPE_LABELS[group.type]}:
                </span>
                <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                  {group.items.map((chip) => (
                    <span key={chip.type + chip.val}
                      style={{ display:"inline-flex", alignItems:"center", gap:5, background:C.secondarySoft,
                        color:C.secondary, border:`1px solid ${C.secondary}40`,
                        padding:"2px 6px 2px 9px", borderRadius:20, fontSize:12, fontWeight:600 }}>
                      {chip.val}
                      <button type="button" onClick={() => removeChip(chip.type, chip.val)}
                        style={{ background:"none", border:"none", cursor:"pointer", color:C.secondary,
                          padding:0, lineHeight:1, fontSize:14, display:"flex", alignItems:"center" }}>×</button>
                    </span>
                  ))}
                </div>
              </div>
            ))}
            <button type="button" onClick={clearFilters}
              style={{ fontSize:11, color:C.muted, background:"none", border:"none",
                cursor:"pointer", textDecoration:"underline", fontFamily:"inherit" }}>
              Limpiar todo
            </button>
          </div>
        )}

        {/* Encabezado resultados */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10, gap:12 }}>

          {/* Izquierda: título + toggle vista */}
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:2 }}>
              <span style={{ fontSize:15, fontWeight:700, color:C.primary }}>{titleLabel}</span>
              <span style={{ background:C.primarySoft, color:C.primary, fontSize:11, fontWeight:700,
                padding:"2px 8px", borderRadius:10 }}>
                {tableLoading ? "..." : `${total} registro${total !== 1 ? "s" : ""}`}
              </span>
            </div>
            <p style={{ fontSize:12, color:C.muted, margin:"0 0 10px" }}>
              {fmtDateISO(queryMeta.fechaInicio)} – {fmtDateISO(queryMeta.fechaFin)}
            </p>
            {/* Toggle vista — claramente separado de los exports */}
            <div style={{ display:"flex", gap:0, border:`1px solid ${C.border}`, borderRadius:6, overflow:"hidden", width:"fit-content" }}>
              {[{ k:"tabla", icon:"☰", lbl:"Tabla" }, { k:"graficas", icon:"◈", lbl:"Gráficas" }].map(({ k, icon, lbl }) => (
                <button key={k} type="button" onClick={() => {
                  setViewMode(k);
                  if (k === "graficas" && !graficasData) {
                    const p = {
                      tipo: tipoConsulta, fecha_inicio: queryMeta.fechaInicio, fecha_fin: queryMeta.fechaFin,
                      modulos: selModulo ? [selModulo] : [],
                      empresas: selEmpresa, abogados: selAbogado, usuarios: selUsuario, ciudades: selCiudad, autoridades: selAutoridad,
                    };
                    fetchGraficas(p);
                  }
                }}
                  style={{ padding:"6px 16px", border:"none", fontFamily:"inherit",
                    fontSize:12, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:6,
                    background: viewMode === k ? C.secondary : C.surface,
                    color: viewMode === k ? "#fff" : C.muted,
                    borderRight: k === "tabla" ? `1px solid ${C.border}` : "none",
                    transition:"all 0.15s" }}>
                  <span style={{ fontSize:11 }}>{icon}</span> {lbl}
                </button>
              ))}
            </div>
          </div>

          {/* Derecha: columnas + exportar */}
          <div style={{ display:"flex", gap:16, alignItems:"flex-start", flexShrink:0 }}>

            {/* Selector de columnas — sólo aplica a la tabla general */}
            {!empDetalle && <div ref={colPanelRef} style={{ position:"relative" }}>
              <span style={{ display:"block", fontSize:10, fontWeight:700, letterSpacing:"0.06em",
                color:C.outline, textTransform:"uppercase", marginBottom:6 }}>Columnas</span>
              <button type="button" onClick={() => setColPanelOpen((v) => !v)}
                style={{ ...S.btnGhost, display:"flex", alignItems:"center", gap:6,
                  borderColor: colPanelOpen ? C.secondary : C.border,
                  color: colPanelOpen ? C.secondary : C.muted }}>
                ⊞ Columnas
                <span style={{ fontSize:10, opacity:0.5,
                  transform: colPanelOpen ? "rotate(180deg)" : "none", transition:"transform 0.15s" }}>▾</span>
              </button>
              {colPanelOpen && (
                <div style={{ ...S.colPanel, right:0 }}>
                  {/* Header */}
                  <div style={{ padding:"8px 14px 6px", display:"flex", alignItems:"center",
                    justifyContent:"space-between", borderBottom:`1px solid ${C.border}` }}>
                    <span style={{ fontSize:10, fontWeight:700, letterSpacing:"0.05em",
                      color:C.muted, textTransform:"uppercase" }}>Columnas · arrastra para reordenar</span>
                    {colOrder && (
                      <button type="button" onClick={() => setColOrder(null)}
                        style={{ fontSize:10, background:"none", border:"none", cursor:"pointer",
                          color:C.secondary, fontWeight:700, fontFamily:"inherit", padding:0 }}>
                        Restablecer
                      </button>
                    )}
                  </div>
                  {/* Items */}
                  {orderedPanelCols.map((col) => {
                    const always = col.key === "ne";
                    const checked = always || isColVisible(col.key);
                    const isTarget = dragOverKey === col.key;
                    return (
                      <div key={col.key}
                        draggable
                        onDragStart={(e) => {
                          dragSrcRef.current = col.key;
                          e.dataTransfer.effectAllowed = "move";
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = "move";
                          if (dragSrcRef.current !== col.key) setDragOverKey(col.key);
                        }}
                        onDragLeave={() => setDragOverKey(null)}
                        onDrop={(e) => {
                          e.preventDefault();
                          reorderCol(dragSrcRef.current, col.key);
                          setDragOverKey(null);
                          dragSrcRef.current = null;
                        }}
                        onDragEnd={() => { setDragOverKey(null); dragSrcRef.current = null; }}
                        style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 14px",
                          userSelect:"none", cursor:"default",
                          borderTop: isTarget ? `2px solid ${C.secondary}` : "2px solid transparent",
                          background: checked && !always ? C.secondarySoft : "transparent",
                          transition:"border-color 0.1s" }}>
                        <span style={{ color:C.outline, fontSize:15, cursor:"grab",
                          flexShrink:0, lineHeight:1 }} title="Arrastra para mover">⠿</span>
                        <input type="checkbox" checked={checked} disabled={always}
                          onChange={(e) => { e.stopPropagation(); !always && toggleColVis(col.key); }}
                          onClick={(e) => e.stopPropagation()}
                          style={{ accentColor:C.secondary,
                            cursor: always ? "default" : "pointer", flexShrink:0 }} />
                        <span style={{ fontSize:13, color: always ? C.outline : C.text, flex:1 }}>
                          {col.label}
                        </span>
                        {always && (
                          <span style={{ fontSize:9, fontWeight:700, letterSpacing:"0.06em",
                            color:C.outline, textTransform:"uppercase" }}>fijo</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>}

            {/* Exportar — sólo aplica a la tabla general; en el detalle de empresa cada pestaña trae sus propios botones */}
            {!empDetalle && (
              <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6 }}>
                <span style={{ fontSize:10, fontWeight:700, letterSpacing:"0.06em", color:C.outline, textTransform:"uppercase" }}>
                  Exportar
                </span>
                <div style={{ display:"flex", gap:8 }}>
                  <button type="button" onClick={exportPDF}
                    style={{ ...S.btnGhost, display:"flex", alignItems:"center", gap:6,
                      color:"#93000a", borderColor:"#ffdad6", background:"#ffdad6" }}>
                    <span style={{ fontSize:13 }}>⬇</span> PDF
                  </button>
                  <button type="button" onClick={exportCSV}
                    style={{ ...S.btnGhost, display:"flex", alignItems:"center", gap:6,
                      color:"#004d00", borderColor:"#c8f5c8", background:"#c8f5c8" }}>
                    <span style={{ fontSize:13 }}>⬇</span> Excel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── VISTA TABLA ─────────────────────────────────────────────────── */}
        {viewMode === "graficas" && (
          <GraficasView
            graficasData={graficasData}
            clientStats={chartStats}
            total={total}
            titleLabel={titleLabel}
            loading={graficasLoading || tableLoading}
          />
        )}

        {/* Detalle de empresa (drill-down) */}
        {viewMode === "tabla" && empDetalle && (
          <EmpresaDetalleView
            empresa={empDetalle}
            data={empDetalleData}
            loading={empDetalleLoading}
            fechaInicio={queryMeta.fechaInicio}
            fechaFin={queryMeta.fechaFin}
            responsablesFiltro={selAbogado}
            onBack={closeEmpresaDetalle}
          />
        )}

        {/* Tabla */}
        {viewMode === "tabla" && !empDetalle && <div style={{ ...S.card, padding:0 }}>
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", minWidth:"max-content" }}>
              <thead>
                <tr style={{ background:"#f0f2f5", borderBottom:`2px solid ${C.border}` }}>
                  {displayCols.map((col) => {
                    if (col.key === "_num") return (
                      <th key="_num" style={{ ...S.th, width:40, textAlign:"center" }}>#</th>
                    );
                    if (false) return null;
                    if (col.key === "modulo") return null;
                    const active = sortCol === col.key;
                    return (
                      <th key={col.key} onClick={() => col.sort && handleSort(col.key)}
                        style={{ ...S.th, color: active ? C.secondary : C.muted,
                          cursor: col.sort ? "pointer" : "default",
                          width: col.w, textAlign: col.align, userSelect:"none" }}>
                        <span style={{ display:"inline-flex", alignItems:"center", gap:4,
                          justifyContent: col.align === "right" ? "flex-end"
                            : col.align === "center" ? "center" : "flex-start" }}>
                          {col.label}
                          {col.sort && (
                            <span style={{ opacity: active ? 1 : 0.3, fontSize:10 }}>
                              {active ? (sortDir === "asc" ? "↑" : "↓") : "⇅"}
                            </span>
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {tableLoading ? (
                  <tr>
                    <td colSpan={99} style={{ padding:"56px 24px", textAlign:"center", color:C.outline }}>
                      <div style={{ fontSize:20, marginBottom:6 }}>⏳</div>
                      <div style={{ fontSize:13 }}>Consultando datos...</div>
                    </td>
                  </tr>
                ) : tableError ? (
                  <tr>
                    <td colSpan={99} style={{ padding:"40px 24px", textAlign:"center" }}>
                      <div style={{ color:C.redText, fontSize:13, marginBottom:10 }}>
                        Error al cargar datos: {tableError}
                      </div>
                      <button onClick={applyFilters} style={S.btnGhost}>Reintentar</button>
                    </td>
                  </tr>
                ) : pageData.length === 0 ? (
                  <tr>
                    <td colSpan={99} style={{ padding:"56px 24px", textAlign:"center", color:C.outline }}>
                      <div style={{ fontSize:28, marginBottom:8 }}>🔍</div>
                      Sin registros para los filtros seleccionados
                    </td>
                  </tr>
                ) : pageData.map((row, idx) => {
                  const gi = (page - 1) * rowsPP + idx;
                  const isEmpresaRow = tipoConsulta === "empresas";
                  return (
                    <tr key={idx}
                      title={isEmpresaRow ? "Ver detalle de la empresa" : undefined}
                      onClick={isEmpresaRow ? () => openEmpresaDetalle(row.empresa) : undefined}
                      style={{ borderBottom:`1px solid ${C.border}`,
                        cursor: isEmpresaRow ? "pointer" : "default",
                        background: idx % 2 === 1 ? "#f8f9fb" : C.surface }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "rgba(10,83,154,0.05)"}
                      onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 1 ? "#f8f9fb" : C.surface}
                    >
                      {displayCols.map((col) => {
                        if (col.key === "_num") return (
                          <td key="_num" style={{ padding:"8px 12px", textAlign:"center", color:C.outline, fontSize:12 }}>
                            {gi + 1}
                          </td>
                        );
                        if (false) return null;
                        if (col.key === "modulo") return null;

                        const val = row[col.key];
                        let content;

                        if (col.key === "ne") {
                          content = (
                            <span style={{ fontWeight:700, color:C.primary, fontFamily:"monospace",
                              fontSize:12, letterSpacing:"0.02em" }}>{val || "—"}</span>
                          );
                        } else if (col.key === "estado") {
                          if (!val) return <td key={col.key} style={{ padding:"8px 12px" }}>—</td>;
                          const st = getStatusStyle(val);
                          content = (
                            <span style={{ background:st.bg, color:st.tc, padding:"2px 8px",
                              borderRadius:4, fontSize:10, fontWeight:700, letterSpacing:"0.05em",
                              textTransform:"uppercase", whiteSpace:"nowrap", display:"inline-block" }}>
                              {val}
                            </span>
                          );
                        } else if (col.key === "modulo") {
                          const mc = MOD_COLOR[val] || { bg:C.gray, tc:C.grayText };
                          content = (
                            <span style={{ background:mc.bg, color:mc.tc, padding:"2px 7px",
                              borderRadius:4, fontSize:10, fontWeight:700, letterSpacing:"0.03em",
                              textTransform:"uppercase", whiteSpace:"nowrap", display:"inline-block" }}>
                              {val}
                            </span>
                          );
                        } else if (col.key === "activos") {
                          content = (
                            <span style={{ background:C.primarySoft, color:C.primary,
                              padding:"2px 8px", borderRadius:10, fontWeight:700, fontSize:12 }}>
                              {val ?? 0}
                            </span>
                          );
                        } else if (col.key === "resueltos") {
                          content = <span style={{ color:C.muted, fontWeight:500 }}>{val ?? 0}</span>;
                        } else if (["diferimiento","convenio","archivo_incomparecencia_trabajador","no_conciliacion","constancia_conciliacion"].includes(col.key)) {
                          content = <span style={{ color:C.muted, fontWeight:500 }}>{val ?? 0}</span>;
                        } else if (["audiencias_creadas","expedientes_creados"].includes(col.key)) {
                          content = <span style={{ color:C.secondary, fontWeight:600 }}>{val ?? 0}</span>;
                        } else if (col.key === "total") {
                          content = <span style={{ fontWeight:700, color:C.text }}>{val ?? 0}</span>;
                        } else if (["proxima_audiencia","fecha_resolucion","fecha_baja","fecha_audiencia"].includes(col.key)) {
                          const d = fmtDate(val);
                          content = d
                            ? <span style={{ fontSize:13 }}>{d}</span>
                            : <span style={{ color:C.outline, fontStyle:"italic", fontSize:12 }}>—</span>;
                        } else if (col.key === "resultado") {
                          content = val
                            ? <span style={{ fontSize:13 }}>{toTitleCase(val)}</span>
                            : <span style={{ color:C.outline }}>—</span>;
                        } else {
                          content = <Trunc text={val ? toTitleCase(val) : null} style={{ fontSize:13 }} />;
                        }

                        return (
                          <td key={col.key} style={{ padding:"8px 12px", textAlign:col.align, verticalAlign:"middle" }}>
                            {content}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          <div style={{ borderTop:`1px solid ${C.border}`, padding:"10px 16px",
            display:"flex", justifyContent:"space-between", alignItems:"center", background:C.surface }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:12, color:C.muted }}>Filas por página:</span>
              <select value={rowsPP} onChange={(e) => { setRowsPP(Number(e.target.value)); setPage(1); }}
                style={{ border:`1px solid ${C.border}`, borderRadius:4, padding:"2px 6px",
                  fontSize:12, color:C.text, cursor:"pointer", outline:"none", fontFamily:"inherit" }}>
                {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <span style={{ fontSize:12, color:C.muted }}>
                {total === 0 ? "0" : `${start}–${end}`} de {total}
              </span>
              <div style={{ display:"flex", gap:4 }}>
                <button onClick={() => page > 1 && setPage((p) => p - 1)} disabled={page <= 1}
                  style={{ ...S.pgBtn, opacity: page > 1 ? 1 : 0.35 }}>‹</button>
                {totalPages > 1 && (
                  <span style={{ display:"flex", alignItems:"center", justifyContent:"center",
                    padding:"0 8px", fontSize:12, fontWeight:700, color:C.primary }}>
                    {page}/{totalPages}
                  </span>
                )}
                <button onClick={() => page < totalPages && setPage((p) => p + 1)} disabled={page >= totalPages}
                  style={{ ...S.pgBtn, opacity: page < totalPages ? 1 : 0.35 }}>›</button>
              </div>
            </div>
          </div>
        </div>}

      </div>
    </div>
  );
}

// ── Exportar PDF (multiselect de tablas) ───────────────────────────────────────
function ExportPdfMenu({ tabs, onExport }) {
  const [open, setOpen]       = useState(false);
  const [checked, setChecked] = useState(() => new Set(tabs.map((t) => t.key)));
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggle = (key) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <div ref={ref} style={{ position:"relative" }}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        style={{ ...S.btnGhost, display:"flex", alignItems:"center", gap:6,
          color:"#93000a", borderColor:"#ffdad6", background:"#ffdad6" }}>
        <span style={{ fontSize:13 }}>⬇</span> PDF
        <span style={{ fontSize:9, opacity:0.6, transform: open ? "rotate(180deg)" : "none", transition:"transform 0.15s" }}>▾</span>
      </button>
      {open && (
        <div style={{ ...S.colPanel, right:0, minWidth:240 }}>
          <div style={{ padding:"8px 12px", borderBottom:`1px solid ${C.border}` }}>
            <span style={{ fontSize:10, fontWeight:700, letterSpacing:"0.05em", color:C.muted, textTransform:"uppercase" }}>
              Selecciona qué imprimir
            </span>
          </div>
          {tabs.map((t) => (
            <label key={t.key} style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 12px",
              fontSize:13, color:C.text, cursor:"pointer",
              background: checked.has(t.key) ? C.secondarySoft : "transparent" }}>
              <input type="checkbox" checked={checked.has(t.key)} onChange={() => toggle(t.key)}
                style={{ accentColor:C.secondary, cursor:"pointer" }} />
              {t.label}
            </label>
          ))}
          <div style={{ padding:"8px 12px", borderTop:`1px solid ${C.border}`,
            display:"flex", justifyContent:"flex-end", gap:8 }}>
            <button type="button" onClick={() => setOpen(false)}
              style={{ ...S.btnGhost, padding:"4px 12px", fontSize:11 }}>
              Cancelar
            </button>
            <button type="button" disabled={checked.size === 0}
              onClick={() => { onExport([...checked]); setOpen(false); }}
              style={{ ...S.btnPrimary, padding:"4px 16px", fontSize:11,
                opacity: checked.size === 0 ? 0.5 : 1,
                cursor: checked.size === 0 ? "not-allowed" : "pointer" }}>
              Aceptar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Selector de columnas reutilizable (por tabla) ──────────────────────────────
function ColumnsMenu({ cols, visible, onToggle, onReset }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div ref={ref} style={{ position:"relative" }}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        style={{ ...S.btnGhost, height:30, padding:"0 10px", display:"flex", alignItems:"center", gap:6,
          fontSize:11, borderColor: open ? C.secondary : C.border, color: open ? C.secondary : C.muted }}>
        ⊞ Columnas
        <span style={{ fontSize:9, opacity:0.5, transform: open ? "rotate(180deg)" : "none", transition:"transform 0.15s" }}>▾</span>
      </button>
      {open && (
        <div style={{ ...S.colPanel, right:0, minWidth:200 }}>
          <div style={{ padding:"7px 12px", display:"flex", alignItems:"center", justifyContent:"space-between",
            borderBottom:`1px solid ${C.border}` }}>
            <span style={{ fontSize:10, fontWeight:700, letterSpacing:"0.05em", color:C.muted, textTransform:"uppercase" }}>
              Columnas
            </span>
            <button type="button" onClick={onReset}
              style={{ fontSize:10, background:"none", border:"none", cursor:"pointer",
                color:C.secondary, fontWeight:700, fontFamily:"inherit", padding:0 }}>
              Mostrar todo
            </button>
          </div>
          {cols.map((c) => {
            const checked = visible === null || visible.has(c.key);
            return (
              <label key={c.key} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 12px",
                fontSize:12, color:C.text, cursor:"pointer",
                background: checked ? C.secondarySoft : "transparent" }}>
                <input type="checkbox" checked={checked} onChange={() => onToggle(c.key)}
                  style={{ accentColor:C.secondary, cursor:"pointer" }} />
                {c.label}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Detalle de empresa (drill-down desde la tabla Empresas) ───────────────────
function EmpresaDetalleView({ empresa, data, loading, fechaInicio, fechaFin, responsablesFiltro, onBack }) {
  const [activeTab, setActiveTab]         = useState("audiencias");
  const [audVisible, setAudVisible]       = useState(null);
  const [asuntosVisible, setAsuntosVisible] = useState(null);
  const [convVisible, setConvVisible]     = useState(null);
  const [expandedKpi, setExpandedKpi]     = useState(null);

  if (loading || !data) {
    return (
      <div style={{ ...S.card, padding:"56px 24px", textAlign:"center", color:C.outline }}>
        <div style={{ fontSize:20, marginBottom:6 }}>⏳</div>
        <div style={{ fontSize:13 }}>Cargando detalle...</div>
      </div>
    );
  }

  const asuntos    = data.asuntos ?? [];
  const audiencias = data.audiencias ?? [];
  const convenios  = data.convenios ?? [];

  const MODULOS = ["Centro de Conciliación", "Tribunal Laboral", "JCA"];
  const countByModulo = (arr) => {
    const counts = { "Centro de Conciliación":0, "Tribunal Laboral":0, "JCA":0 };
    arr.forEach((x) => {
      const m = x.modulo || "Centro de Conciliación";
      counts[m] = (counts[m] ?? 0) + 1;
    });
    return counts;
  };

  const kpiBreakdowns = {
    total:         countByModulo(asuntos),
    audiencias:    countByModulo(audiencias),
    convenios:     countByModulo(convenios),
    cumplimientos: countByModulo(convenios.filter((c) => c.estado === "Convenio Cumplido")),
    activos:       countByModulo(asuntos.filter((a) => a.activo)),
  };

  const kpis = [
    { key:"total",         lbl:"Total Expedientes", val: data.kpis?.asuntos_con_actividad ?? 0, color: C.secondary },
    { key:"audiencias",    lbl:"Audiencias",         val: data.kpis?.audiencias ?? 0,            color: "#8b5cf6" },
    { key:"convenios",     lbl:"Convenios",          val: data.kpis?.convenios ?? 0,             color: "#f97316" },
    { key:"cumplimientos", lbl:"Cumplimientos",      val: data.kpis?.cumplimientos ?? 0,         color: "#0f766e" },
    { key:"activos",       lbl:"Asuntos activos",    val: data.kpis?.asuntos_activos ?? 0,       color: "#3b82f6" },
  ];

  const ciudades = [...new Set(asuntos.map((a) => a.ciudad).filter(Boolean))].join(", ") || "—";

  // Los responsables sólo se muestran (en pantalla y en el PDF) si el usuario
  // seleccionó explícitamente uno o más en el filtro RESPONSABLE; si no hay
  // selección, se omiten por completo.
  const hayFiltroResponsable = (responsablesFiltro?.length || 0) > 0;
  const responsables = hayFiltroResponsable
    ? [...new Set(asuntos.map((a) => a.abogado).filter(Boolean))].join(", ") || "—"
    : "";

  const gruposAudiencias = Object.entries(
    audiencias.reduce((acc, a) => {
      const m = a.modulo || "Centro de Conciliación";
      (acc[m] = acc[m] || []).push(a);
      return acc;
    }, {})
  );

  const thBlock = { padding:"5px 16px 7px", fontSize:10, fontWeight:700, letterSpacing:"0.05em",
    color:C.muted, whiteSpace:"nowrap", textAlign:"left" };
  const tdBlock = { padding:"7px 16px", fontSize:13, color:C.text };
  const tdMes = { padding:"9px 16px", fontSize:12, fontWeight:800, letterSpacing:"0.04em", color:C.primary,
    background:C.primarySoft, borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}` };
  const filaMes = (mes, colSpan, key) => (
    <tr key={key}>
      <td colSpan={colSpan} style={tdMes}>
        {mes.label} <span style={{ fontWeight:600, color:C.muted, marginLeft:6 }}>· {mes.count} registro{mes.count === 1 ? "" : "s"}</span>
      </td>
    </tr>
  );

  const TABS = [
    { key:"audiencias", label:"Audiencias",               count:audiencias.length, cols:SECTION_COLUMNS.audiencias, visible:audVisible,     setVisible:setAudVisible,     rows:audiencias },
    { key:"asuntos",    label:"Total Expedientes",         count:asuntos.length,    cols:SECTION_COLUMNS.asuntos,    visible:asuntosVisible, setVisible:setAsuntosVisible, rows:asuntos },
    { key:"convenios",  label:"Convenios y cumplimientos", count:convenios.length,  cols:SECTION_COLUMNS.convenios,  visible:convVisible,    setVisible:setConvVisible,    rows:convenios },
  ];
  const current = TABS.find((t) => t.key === activeTab);

  const isColVisible = (tab, key) => tab.visible === null || tab.visible.has(key);
  const toggleCol = (tab, key) => {
    tab.setVisible((prev) => {
      const cur = prev ?? new Set(tab.cols.map((c) => c.key));
      const next = new Set(cur);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleExportSelected = (selectedKeys) => {
    printEmpresaSeccionesPdf({
      sections: selectedKeys,
      empresa,
      fechaInicio,
      fechaFin,
      ciudadesLabel: ciudades === "—" ? "" : ciudades,
      responsablesLabel: responsables === "—" ? "" : responsables,
      dataBySection: { audiencias, asuntos, convenios },
      visibleKeysBySection: { audiencias: audVisible, asuntos: asuntosVisible, convenios: convVisible },
      kpis: kpis.map((k) => ({ lbl: k.lbl, val: k.val, color: k.color })),
    });
  };

  const visCols = current.cols.filter((c) => isColVisible(current, c.key));

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:12 }}>

      <div style={S.card}>
        <button type="button" onClick={onBack}
          style={{ background:"none", border:"none", padding:0, margin:"0 0 12px", color:C.secondary,
            fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit",
            display:"flex", alignItems:"center", gap:6 }}>
          ‹ Volver a Empresas
        </button>
        <div style={{ fontSize:10, fontWeight:700, letterSpacing:"0.08em", color:C.outline,
          textTransform:"uppercase", marginBottom:6 }}>Informe de actividad</div>
        <h3 style={{ margin:0, fontSize:20, fontWeight:700, color:C.primary, letterSpacing:"-0.01em" }}>
          {empresa}
        </h3>
        <div style={{ display:"flex", flexWrap:"wrap", gap:16, marginTop:10, fontSize:12, color:C.muted }}>
          <span><span style={{ color:C.outline }}>Periodo:</span>{" "}
            <strong style={{ color:C.text }}>{fmtDateISO(fechaInicio)} – {fmtDateISO(fechaFin)}</strong></span>
          <span><span style={{ color:C.outline }}>Ciudades:</span> {ciudades}</span>
          {hayFiltroResponsable && (
            <span><span style={{ color:C.outline }}>Responsables:</span> {responsables}</span>
          )}
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:10 }}>
        {kpis.map((k) => {
          const isOpen = expandedKpi === k.key;
          return (
            <div key={k.key} role="button" tabIndex={0}
              onClick={() => setExpandedKpi((prev) => (prev === k.key ? null : k.key))}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setExpandedKpi((prev) => (prev === k.key ? null : k.key)); }}
              style={{ ...S.card, padding:"14px 16px", cursor:"pointer", userSelect:"none",
                border: isOpen ? `2px solid ${k.color}` : S.card.border,
                transition:"border-color 0.15s" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                <div style={{ fontSize:10, fontWeight:700, letterSpacing:"0.06em", color:C.outline,
                  textTransform:"uppercase" }}>{k.lbl}</div>
                <span style={{ fontSize:10, color: isOpen ? k.color : C.outline, transition:"transform 0.15s",
                  transform: isOpen ? "rotate(180deg)" : "none" }}>▾</span>
              </div>
              <div style={{ fontSize:26, fontWeight:800, lineHeight:1, color:k.color }}>{k.val}</div>
            </div>
          );
        })}
      </div>

      {expandedKpi && (() => {
        const activeK = kpis.find((k) => k.key === expandedKpi);
        const bd = kpiBreakdowns[expandedKpi] || {};
        return (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:10 }}>
            {MODULOS.map((m) => {
              const mc = MOD_COLOR[m] || { bg:C.gray, tc:C.grayText };
              return (
                <div key={m} style={{ ...S.card, padding:"12px 16px", borderLeft:`4px solid ${mc.tc}` }}>
                  <span style={{ background:mc.bg, color:mc.tc, padding:"2px 8px", borderRadius:4,
                    fontSize:10, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase" }}>{m}</span>
                  <div style={{ fontSize:22, fontWeight:800, marginTop:8, color:C.text }}>{bd[m] ?? 0}</div>
                  <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>de {activeK?.lbl.toLowerCase()}</div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Pestañas de tablas + exportar PDF (fuera de las pestañas, multiselect) */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, flexWrap:"wrap" }}>
        <div style={{ display:"flex", gap:0, border:`1px solid ${C.border}`, borderRadius:6, overflow:"hidden", width:"fit-content" }}>
          {TABS.map((t, i) => (
            <button key={t.key} type="button" onClick={() => setActiveTab(t.key)}
              style={{ padding:"8px 16px", border:"none", fontFamily:"inherit", fontSize:12, fontWeight:700,
                cursor:"pointer", display:"flex", alignItems:"center", gap:6,
                background: activeTab === t.key ? C.secondary : C.surface,
                color: activeTab === t.key ? "#fff" : C.muted,
                borderRight: i === TABS.length - 1 ? "none" : `1px solid ${C.border}`,
                transition:"all 0.15s" }}>
              {t.label}
            </button>
          ))}
        </div>
        <ExportPdfMenu tabs={TABS.map((t) => ({ key:t.key, label:t.label }))} onExport={handleExportSelected} />
      </div>

      {/* Tabla de la pestaña activa */}
      <div style={{ ...S.card, padding:0 }}>
        <div style={{ padding:"11px 16px", borderBottom:`1px solid ${C.border}`,
          display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, flexWrap:"wrap" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:13, fontWeight:700, color:C.primary }}>{current.label}</span>
            <span style={{ background:C.primarySoft, color:C.primary, fontSize:11, fontWeight:700,
              padding:"2px 8px", borderRadius:10 }}>{current.count}</span>
          </div>
          <ColumnsMenu cols={current.cols} visible={current.visible}
            onToggle={(key) => toggleCol(current, key)}
            onReset={() => current.setVisible(null)} />
        </div>

        {activeTab === "audiencias" && (
          <>
            {gruposAudiencias.length === 0 && (
              <div style={{ padding:"9px 16px", fontSize:12, color:C.outline, fontStyle:"italic" }}>
                Sin audiencias en el periodo
              </div>
            )}
            {gruposAudiencias.map(([modulo, items], gi) => {
              const mc = MOD_COLOR[modulo] || { bg:C.gray, tc:C.grayText };
              return (
                <div key={modulo} style={{ padding:"6px 0 16px", borderTop: gi === 0 ? "none" : `1px solid ${C.border}` }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 16px 8px" }}>
                    <span style={{ background:mc.bg, color:mc.tc, padding:"3px 9px", borderRadius:4,
                      fontSize:10, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase" }}>
                      {modulo}
                    </span>
                    <span style={{ fontSize:13, fontWeight:700, color:C.primary }}>
                      {items.length} audiencia{items.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div style={{ overflowX:"auto" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse" }}>
                      <thead>
                        <tr>
                          {visCols.map((c) => <th key={c.key} style={thBlock}>{c.label}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {conEncabezadosMes(items, "fecha_audiencia").map((a, i) => a.__mes ? filaMes(a, visCols.length || 1, `mes-${a.label}`) : (
                          <tr key={i} style={{ borderTop:"1px solid #f0f2f5", background: i % 2 === 1 ? "#f8f9fb" : C.surface }}>
                            {visCols.map((c) => {
                              if (c.key === "fecha_audiencia") return <td key={c.key} style={tdBlock}>{fmtDate(a.fecha_audiencia) || "—"}</td>;
                              if (c.key === "ne") return <td key={c.key} style={{ ...tdBlock, fontWeight:700, color:C.primary, fontFamily:"monospace", fontSize:12 }}>{a.ne || "—"}</td>;
                              if (c.key === "estado") return <td key={c.key} style={tdBlock}>{a.estado ? toTitleCase(a.estado) : "—"}</td>;
                              if (c.key === "tipo_audiencia") return <td key={c.key} style={tdBlock}>{a.tipo_audiencia ? toTitleCase(a.tipo_audiencia) : "—"}</td>;
                              if (c.key === "autoridad") return <td key={c.key} style={tdBlock}><Trunc text={a.autoridad ? toTitleCase(a.autoridad) : null} max={200} /></td>;
                              if (c.key === "abogado") return <td key={c.key} style={tdBlock}><Trunc text={a.abogado ? toTitleCase(a.abogado) : null} max={200} /></td>;
                              return <td key={c.key} style={tdBlock}>{a[c.key] ?? "—"}</td>;
                            })}
                          </tr>
                        ))}
                        {items.length === 0 && (
                          <tr><td colSpan={visCols.length || 1} style={{ padding:"9px 16px", fontSize:12, color:C.outline, fontStyle:"italic" }}>
                            Sin audiencias en el periodo
                          </td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </>
        )}

        {activeTab === "asuntos" && (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", minWidth:"max-content" }}>
              <thead>
                <tr style={{ background:"#f0f2f5", borderBottom:`2px solid ${C.border}` }}>
                  {visCols.map((c) => <th key={c.key} style={S.th}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {conEncabezadosMes(asuntos, "fecha").map((x, i) => {
                  if (x.__mes) return filaMes(x, visCols.length || 1, `mes-${x.label}`);
                  const mc  = MOD_COLOR[x.modulo] || { bg:C.gray, tc:C.grayText };
                  const est = getStatusStyle(x.estado);
                  return (
                    <tr key={i} style={{ borderBottom:`1px solid ${C.border}`, background: i % 2 === 1 ? "#f8f9fb" : C.surface }}>
                      {visCols.map((c) => {
                        if (c.key === "ne") return <td key={c.key} style={{ padding:"8px 12px", fontWeight:700, color:C.primary, fontFamily:"monospace", fontSize:12 }}>{x.ne || "—"}</td>;
                        if (c.key === "modulo") return (
                          <td key={c.key} style={{ padding:"8px 12px" }}>
                            <span style={{ background:mc.bg, color:mc.tc, padding:"2px 7px", borderRadius:4,
                              fontSize:10, fontWeight:700, letterSpacing:"0.03em", textTransform:"uppercase" }}>{x.modulo}</span>
                          </td>
                        );
                        if (c.key === "trabajador") return <td key={c.key} style={{ padding:"8px 12px" }}><Trunc text={x.trabajador ? toTitleCase(x.trabajador) : null} max={200} style={{ fontSize:13 }} /></td>;
                        if (c.key === "abogado") return <td key={c.key} style={{ padding:"8px 12px" }}><Trunc text={x.abogado ? toTitleCase(x.abogado) : null} max={200} style={{ fontSize:13 }} /></td>;
                        if (c.key === "autoridad") return <td key={c.key} style={{ padding:"8px 12px" }}><Trunc text={x.autoridad ? toTitleCase(x.autoridad) : null} max={200} style={{ fontSize:13, color:C.muted }} /></td>;
                        if (c.key === "audiencias_periodo") return <td key={c.key} style={{ padding:"8px 12px", textAlign:"center" }}>{x.audiencias_periodo || 0}</td>;
                        if (c.key === "fecha") return <td key={c.key} style={{ padding:"8px 12px", fontSize:13, color:C.muted }}>{fmtDate(x.fecha) || "—"}</td>;
                        if (c.key === "estado") return (
                          <td key={c.key} style={{ padding:"8px 12px", textAlign:"center" }}>
                            {x.estado ? (
                              <span style={{ background:est.bg, color:est.tc, padding:"2px 8px", borderRadius:4,
                                fontSize:10, fontWeight:700, letterSpacing:"0.05em", textTransform:"uppercase" }}>{toTitleCase(x.estado)}</span>
                            ) : "—"}
                          </td>
                        );
                        return <td key={c.key} style={{ padding:"8px 12px", fontSize:13 }}>{x[c.key] ?? "—"}</td>;
                      })}
                    </tr>
                  );
                })}
                {asuntos.length === 0 && (
                  <tr><td colSpan={visCols.length || 1} style={{ padding:"40px 24px", textAlign:"center", color:C.outline }}>
                    Sin asuntos en el periodo
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "convenios" && (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", minWidth:"max-content" }}>
              <thead>
                <tr style={{ background:"#f0f2f5", borderBottom:`2px solid ${C.border}` }}>
                  {visCols.map((c) => <th key={c.key} style={S.th}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {conEncabezadosMes(convenios, "fecha").map((cv, i) => {
                  if (cv.__mes) return filaMes(cv, visCols.length || 1, `mes-${cv.label}`);
                  const est = getStatusStyle(cv.estado);
                  return (
                    <tr key={i} style={{ borderBottom:`1px solid ${C.border}`, background: i % 2 === 1 ? "#f8f9fb" : C.surface }}>
                      {visCols.map((c) => {
                        if (c.key === "ne") return <td key={c.key} style={{ padding:"8px 12px", fontWeight:700, color:C.primary, fontFamily:"monospace", fontSize:12 }}>{cv.ne || "—"}</td>;
                        if (c.key === "fecha") return <td key={c.key} style={{ padding:"8px 12px", fontSize:13 }}>{fmtDate(cv.fecha) || "—"}</td>;
                        if (c.key === "trabajador") return <td key={c.key} style={{ padding:"8px 12px" }}><Trunc text={cv.trabajador ? toTitleCase(cv.trabajador) : null} max={200} style={{ fontSize:13 }} /></td>;
                        if (c.key === "monto") return <td key={c.key} style={{ padding:"8px 12px", fontSize:13, fontWeight:700, color:C.text, textAlign:"right" }}>{fmtMoney(cv.monto)}</td>;
                        if (c.key === "pagos") return <td key={c.key} style={{ padding:"8px 12px", fontSize:13, textAlign:"center" }}>{cv.pagos || "—"}</td>;
                        if (c.key === "estado") return (
                          <td key={c.key} style={{ padding:"8px 12px" }}>
                            <span style={{ background:est.bg, color:est.tc, padding:"2px 8px", borderRadius:4,
                              fontSize:10, fontWeight:700, letterSpacing:"0.05em", textTransform:"uppercase" }}>{cv.estado}</span>
                          </td>
                        );
                        return <td key={c.key} style={{ padding:"8px 12px", fontSize:13 }}>{cv[c.key] ?? "—"}</td>;
                      })}
                    </tr>
                  );
                })}
                {convenios.length === 0 && (
                  <tr><td colSpan={visCols.length || 1} style={{ padding:"9px 16px", fontSize:12, color:C.outline, fontStyle:"italic" }}>
                    Sin convenios en el periodo
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}

// ── Componente de Gráficas ────────────────────────────────────────────────────
function GraficasView({ graficasData, clientStats, total, titleLabel, loading }) {
  if (loading) return (
    <div style={{ textAlign:"center", padding:60, color:"#8a909e", fontSize:14 }}>Cargando gráficas...</div>
  );

  const CV = C;

  // Helpers para construir datasets de chart.js
  const mkHbar = (rows, keyField, valField, color, label) => ({
    labels: rows.map((r) => r[keyField]),
    datasets: [{ label, data: rows.map((r) => r[valField]),
      backgroundColor: color, borderRadius: 4, borderSkipped: false }],
  });

  const mkVbar = (rows, keyField, valField, color, label) => ({
    labels: rows.map((r) => r[keyField]),
    datasets: [{ label, data: rows.map((r) => r[valField]),
      backgroundColor: color, borderRadius: 4, borderSkipped: false }],
  });

  const mkHbarEntries = (entries, color, label) => ({
    labels: entries.map(([k]) => k),
    datasets: [{ label, data: entries.map(([, v]) => v),
      backgroundColor: color, borderRadius: 4, borderSkipped: false }],
  });

  const hbarOpts = (suffix = "registros") => ({
    indexAxis: "y", responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => `  ${ctx.raw} ${suffix}` } },
    },
    scales: {
      x: { grid: { color:"#f1f5f9" }, ticks: { font:{ size:11 }, stepSize:1 } },
      y: { grid: { display:false }, ticks: { font:{ size:11 } } },
    },
  });

  const vbarOpts = (suffix = "audiencias") => ({
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => `  ${ctx.raw} ${suffix}` } },
    },
    scales: {
      x: { grid: { display:false }, ticks: { font:{ size:10 }, maxRotation:45 } },
      y: { grid: { color:"#f1f5f9" }, ticks: { font:{ size:11 }, stepSize:1 } },
    },
  });

  const kpiEmp  = clientStats?.topEmpresas.length ?? 0;
  const kpiCiu  = clientStats?.porCiudad.length ?? 0;
  const kpiAud  = graficasData?.top_abogados_aud.length ?? 0;
  const kpiAuts = graficasData?.por_autoridad.length ?? 0;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

      {/* KPIs */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12 }}>
        {[
          { lbl:"TOTAL EXPEDIENTES",   val:total,   color:CV.secondary },
          { lbl:"EMPRESAS",            val:kpiEmp,  color:"#3b82f6" },
          { lbl:"CIUDADES",            val:kpiCiu,  color:"#f97316" },
          { lbl:"AUTORIDADES",         val:kpiAuts, color:"#8b5cf6" },
        ].map(({ lbl, val, color }) => (
          <div key={lbl} style={{ background:CV.surface, border:`1px solid ${CV.border}`,
            borderRadius:8, padding:"14px 16px", boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:"0.06em", color:CV.outline, marginBottom:6 }}>
              {lbl}
            </div>
            <div style={{ fontSize:28, fontWeight:800, color, lineHeight:1 }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Fila 1: top empresas + audiencias por mes */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        <ChartCard title="Top razones sociales" height={Math.max(180, (clientStats?.topEmpresas.length || 0) * 32)}>
          {clientStats?.topEmpresas.length > 0
            ? <Bar data={mkHbarEntries(clientStats.topEmpresas, CV.secondary, "exp.")} options={hbarOpts("expedientes")} />
            : <Empty label="Sin datos" />}
        </ChartCard>
        <ChartCard title="Audiencias por mes" height={220}>
          {graficasData?.por_mes.length > 0
            ? <Bar data={mkVbar(graficasData.por_mes, "mes_label", "total", "#38bdf8", "audiencias")} options={vbarOpts()} />
            : <Empty label={graficasData ? "Sin audiencias en el rango" : "Aplica filtros para ver gráficas"} />}
        </ChartCard>
      </div>

      {/* Fila 2: abogados en audiencias + por autoridad */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        <ChartCard title="Abogados con más audiencias" height={Math.max(180, (graficasData?.top_abogados_aud.length || 0) * 34)}>
          {graficasData?.top_abogados_aud.length > 0
            ? <Bar data={mkHbar(graficasData.top_abogados_aud, "nombre", "total", "#a78bfa", "audiencias")} options={hbarOpts("audiencias")} />
            : <Empty label={graficasData ? "Sin datos de audiencias" : "Aplica filtros para ver gráficas"} />}
        </ChartCard>
        <ChartCard title="Expedientes por autoridad" height={Math.max(180, (graficasData?.por_autoridad.length || 0) * 34)}>
          {graficasData?.por_autoridad.length > 0
            ? <Bar data={mkHbar(graficasData.por_autoridad, "autoridad", "total", "#f97316", "expedientes")} options={hbarOpts("expedientes")} />
            : <Empty label={graficasData ? "Sin datos de autoridad" : "Aplica filtros para ver gráficas"} />}
        </ChartCard>
      </div>

      {/* Fila 3: tipo notificado + tipo actuario */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        <ChartCard title="Por tipo notificado" height={Math.max(160, (graficasData?.por_tipo_notif.length || 0) * 36)}>
          {graficasData?.por_tipo_notif.length > 0
            ? <Bar data={mkHbar(graficasData.por_tipo_notif, "tipo", "total", "#34d399", "expedientes")} options={hbarOpts("expedientes")} />
            : <Empty label={graficasData ? "Sin datos" : "Aplica filtros para ver gráficas"} />}
        </ChartCard>
        <ChartCard title="Por tipo actuario" height={Math.max(160, (graficasData?.por_tipo_actuario.length || 0) * 36)}>
          {graficasData?.por_tipo_actuario.length > 0
            ? <Bar data={mkHbar(graficasData.por_tipo_actuario, "tipo", "total", "#fb923c", "expedientes")} options={hbarOpts("expedientes")} />
            : <Empty label={graficasData ? "Sin datos" : "Aplica filtros para ver gráficas"} />}
        </ChartCard>
      </div>

      {/* Fila 4: por objeto (ancho completo) */}
      <ChartCard title="Expedientes por objeto" height={Math.max(160, (graficasData?.por_objeto.length || 0) * 36)}>
        {graficasData?.por_objeto.length > 0
          ? <Bar data={mkHbar(graficasData.por_objeto, "objeto", "total", "#60a5fa", "expedientes")} options={hbarOpts("expedientes")} />
          : <Empty label={graficasData ? "Sin datos de objeto" : "Aplica filtros para ver gráficas"} />}
      </ChartCard>

    </div>
  );
}

function ChartCard({ title, height, children }) {
  return (
    <div style={{ background:C.surface, border:`1px solid ${C.border}`,
      borderRadius:8, padding:"16px 18px", boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
      <div style={{ fontSize:12, fontWeight:700, color:C.muted, letterSpacing:"0.04em",
        marginBottom:14, textTransform:"uppercase" }}>{title}</div>
      <div style={{ height }}>{children}</div>
    </div>
  );
}

function Empty({ label }) {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center",
      height:"100%", color:C.outline, fontSize:13, fontStyle:"italic" }}>{label}</div>
  );
}

// ── Estilos base ──────────────────────────────────────────────────────────────
const S = {
  card: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    padding: 16,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  label: {
    display: "block",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.06em",
    color: C.muted,
    marginBottom: 4,
    textTransform: "uppercase",
  },
  input: {
    width: "100%",
    height: 34,
    padding: "0 10px",
    border: `1px solid ${C.border}`,
    borderRadius: 5,
    fontSize: 13,
    color: C.text,
    background: C.surface,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  select: {
    width: "100%",
    height: 34,
    padding: "0 28px 0 10px",
    border: `1px solid ${C.border}`,
    borderRadius: 5,
    fontSize: 13,
    color: C.text,
    background: C.surface,
    outline: "none",
    appearance: "none",
    cursor: "pointer",
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  selArrow: {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none",
    fontSize: 13,
    color: C.outline,
  },
  btnPrimary: {
    height: 34,
    padding: "0 18px",
    background: C.primary,
    color: "#fff",
    border: "none",
    borderRadius: 5,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.03em",
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },
  btnGhost: {
    height: 34,
    padding: "0 14px",
    background: C.surface,
    color: C.muted,
    border: `1px solid ${C.border}`,
    borderRadius: 5,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },
  th: {
    padding: "9px 12px",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.05em",
    color: C.muted,
    whiteSpace: "nowrap",
    textAlign: "left",
  },
  dropdown: {
    position: "absolute",
    top: "calc(100% + 3px)",
    left: 0,
    zIndex: 9999,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
    minWidth: 220,
    maxHeight: 300,
    display: "flex",
    flexDirection: "column",
  },
  colPanel: {
    position: "absolute",
    top: "calc(100% + 4px)",
    right: 0,
    zIndex: 9999,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
    minWidth: 230,
    maxHeight: 360,
    overflowY: "auto",
  },
  pgBtn: {
    width: 30,
    height: 30,
    border: `1px solid ${C.border}`,
    borderRadius: 5,
    background: C.surface,
    cursor: "pointer",
    fontSize: 16,
    color: C.muted,
    fontFamily: "inherit",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
};
